import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { CrmAccount, CrmClient, CrmClientCommunication, CrmRoleOption } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { gccCrNumberValidator, gccRegistrationError, gccVatNumberValidator } from '../../crm/crm-gcc-registration.validation';
import { City } from '../../models/common/city.model';
import { Country } from '../../models/common/country.model';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { SessionStorageService } from '../../services/session-storage.service';

export type CrmQuickCreateMode = 'client' | 'account' | 'contact';
export type CrmQuickCreateResult =
    | { mode: 'client'; record: CrmClient }
    | { mode: 'account'; record: CrmAccount }
    | { mode: 'contact'; record: CrmClientCommunication };

@Component({
    selector: 'app-crm-quick-create-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, ButtonModule, DialogModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputTextModule, MultiSelectModule, SelectModule],
    providers: [BaseMasterDataService],
    templateUrl: './crm-quick-create-dialog.component.html',
    styleUrls: ['./crm-quick-create-dialog.component.css']
})
export class CrmQuickCreateDialogComponent implements OnChanges, OnInit {
    private fb = inject(FormBuilder);
    private readonly nonBlank: ValidatorFn = control => String(control.value ?? '').trim() ? null : { required: true };
    @Input() visible = false;
    @Input() mode: CrmQuickCreateMode = 'client';
    @Input() owningCompanyId?: number;
    @Input() client: CrmClient | null = null;
    @Input() account: CrmAccount | null = null;
    @Input() allowedRoles: string[] = ['CUSTOMER'];
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() created = new EventEmitter<CrmQuickCreateResult>();

    countries: Country[] = [];
    cities: City[] = [];
    roles: CrmRoleOption[] = [];
    saving = false;
    errorMessage = '';
    submitted = false;

    readonly form = this.fb.group({
        companyName: [''],
        customerNameLocal: [''],
        notifyingPersonName: [''],
        primaryEmail: ['', Validators.email],
        phoneNumber: [''],
        businessType: ['B2B'],
        roles: [[] as string[]],
        countryId: [null as number | null],
        cityId: [null as number | null],
        street1: [''],
        street1Local: [''],
        cityLocal: [''],
        postalCode: [''],
        crNumber: [null as number | null],
        taxRegNumber: [''],
        name: [''],
        email: ['', Validators.email],
        phone: [''],
        mobile: ['']
    });

    constructor(
        private crm: CrmService,
        private masterData: BaseMasterDataService,
        private sessionStorage: SessionStorageService
    ) {}

    ngOnInit(): void {
        this.countries = this.sessionStorage.getCountryList() || [];
        if (!this.countries.length) {
            this.masterData.getCountries('en-US').subscribe(countries => {
                this.countries = countries || [];
                this.refreshRegistrationValidators();
            });
        }
        this.refreshRegistrationValidators();
        this.crm.getCrmRoles().subscribe({
            next: roles => this.roles = roles || [],
            error: () => this.roles = []
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible']?.currentValue === true) this.reset();
    }

    get title(): string {
        return this.mode === 'client' ? 'Create CRM Client' : this.mode === 'account' ? 'Create CRM Client Account' : 'Create CRM Client Contact';
    }

    get summaryName(): string {
        return (this.mode === 'contact' ? this.form.value.name : this.form.value.companyName)?.trim() || this.title;
    }

    get owningCompanyName(): string {
        const company = this.sessionStorage.getCompany();
        return company && company.id === this.owningCompanyId ? company.name : `#${this.owningCompanyId || '-'}`;
    }

    get summaryText(): string {
        if (this.mode === 'client') return 'Create a CRM client and its linked commercial party for this company.';
        if (this.mode === 'account') return 'Create an account under the selected CRM client.';
        return this.account ? 'Create a contact and assign it to this client account.' : 'Create a contact under the selected CRM client.';
    }

    get selectedCountry(): Country | null {
        return this.countries.find(country => country.id === this.form.value.countryId) || null;
    }

    get requiresFullIdentity(): boolean {
        return !(this.form.value.roles || []).some(role => role.includes('SUPPLIER'));
    }

    get b2bClient(): boolean {
        return this.requiresFullIdentity && this.form.value.businessType === 'B2B';
    }

    invalid(name: string): boolean {
        const control = this.form.get(name);
        return !!control?.invalid && (this.submitted || control.touched);
    }

    registrationError(name: string): string | null {
        return gccRegistrationError(this.form.get(name));
    }

    onCountryChange(countryId: number | null): void {
        this.form.patchValue({ cityId: null });
        this.cities = [];
        this.refreshRegistrationValidators();
        if (countryId) {
            this.masterData.getCities('en-US', countryId).subscribe(cities => this.cities = cities || []);
        }
    }

    onRolesOrBusinessTypeChange(): void {
        this.syncValidators();
    }

    close(): void {
        if (this.saving) return;
        this.visible = false;
        this.visibleChange.emit(false);
    }

    save(): void {
        if (this.saving) return;
        this.submitted = true;
        this.errorMessage = '';
        this.syncValidators();
        if (!this.owningCompanyId || (this.mode !== 'client' && !this.client?.id) || this.form.invalid) {
            this.form.markAllAsTouched();
            this.errorMessage = 'Complete the required fields before saving.';
            return;
        }
        if (this.mode === 'client' && !(this.form.value.roles || []).some(role => this.allowedRoles.includes(role))) {
            this.errorMessage = 'Select at least one business role supported by this picker.';
            return;
        }
        this.saving = true;
        if (this.mode === 'client') {
            const value = this.form.getRawValue();
            const country = this.selectedCountry;
            this.crm.quickCreateClient({
                client: {
                    companyId: this.owningCompanyId,
                    companyName: value.companyName!.trim(),
                    customerNameLocal: value.customerNameLocal?.trim() || '',
                    notifyingPersonName: value.notifyingPersonName?.trim() || '',
                    primaryEmail: value.primaryEmail?.trim() || '',
                    phoneNumber: value.phoneNumber?.trim() || '',
                    businessType: value.businessType as 'B2B' | 'B2C',
                    roles: value.roles || [],
                    countryId: value.countryId!,
                    currencyCode: country?.currencyCode || 'SAR',
                    exchangeRate: 1,
                    postingDate: new Date(),
                    crNumber: value.crNumber || undefined,
                    taxRegNumber: value.taxRegNumber?.trim() || '',
                    postalCode: value.postalCode?.trim() || '',
                    category: 'STANDARD',
                    website: '',
                    source: 'QUICK_CREATE'
                } as CrmClient,
                address: {
                    countryId: value.countryId!,
                    cityId: value.cityId,
                    street1: value.street1?.trim() || '',
                    street1Local: value.street1Local?.trim() || '',
                    cityLocal: value.cityLocal?.trim() || '',
                    postalCode: value.postalCode?.trim() || ''
                }
            }).pipe(finalize(() => this.saving = false)).subscribe({
                next: response => this.complete('client', response),
                error: error => this.fail(error)
            });
        } else if (this.mode === 'account') {
            this.crm.addAccount({ companyId: this.owningCompanyId, crmClientId: this.client!.id, companyName: this.form.value.companyName!.trim(), category: '', source: 'QUICK_CREATE' })
                .pipe(finalize(() => this.saving = false)).subscribe({
                    next: response => this.complete('account', response),
                    error: error => this.fail(error)
                });
        } else {
            const value = this.form.value;
            this.crm.quickCreateContact(this.client!.id!, this.owningCompanyId, {
                accountId: this.account?.id,
                name: value.name!.trim(),
                email: value.email?.trim() || undefined,
                phone: value.phone?.trim() || undefined,
                mobile: value.mobile?.trim() || undefined
            }).pipe(finalize(() => this.saving = false)).subscribe({
                next: response => this.complete('contact', response),
                error: error => this.fail(error)
            });
        }
    }

    private complete(mode: CrmQuickCreateMode, response: { success: boolean; data?: unknown; message?: string }): void {
        if (!response.success || !response.data) {
            this.errorMessage = response.message || `Unable to create CRM ${mode}.`;
            return;
        }
        if (mode === 'client') this.created.emit({ mode, record: response.data as CrmClient });
        if (mode === 'account') this.created.emit({ mode, record: response.data as CrmAccount });
        if (mode === 'contact') this.created.emit({ mode, record: response.data as CrmClientCommunication });
        this.saving = false;
        this.close();
    }

    private fail(error: any): void {
        this.errorMessage = error?.error?.message || error?.error?.detail || error?.message || `Unable to create CRM ${this.mode}.`;
    }

    reset(): void {
        this.errorMessage = '';
        this.submitted = false;
        this.form.reset({
            companyName: '', customerNameLocal: '', notifyingPersonName: '', primaryEmail: '', phoneNumber: '',
            businessType: 'B2B', roles: this.allowedRoles.length ? [this.allowedRoles[0]] : [],
            countryId: this.sessionStorage.getCompany()?.countryId || null,
            cityId: null, street1: '', street1Local: '', cityLocal: '', postalCode: '', crNumber: null, taxRegNumber: '',
            name: '', email: '', phone: '', mobile: ''
        });
        this.syncValidators();
        if (this.mode === 'client') this.onCountryChange(this.form.value.countryId || null);
    }

    private syncValidators(): void {
        const client = this.mode === 'client';
        const fullIdentity = client && this.requiresFullIdentity;
        const b2b = client && this.b2bClient;
        this.setValidators('companyName', client || this.mode === 'account' ? [this.nonBlank] : []);
        this.setValidators('roles', client ? [Validators.required] : []);
        this.setValidators('countryId', client ? [Validators.required] : []);
        for (const name of ['customerNameLocal', 'notifyingPersonName', 'street1', 'street1Local', 'cityLocal']) {
            this.setValidators(name, fullIdentity ? [this.nonBlank] : []);
        }
        this.setValidators('cityId', fullIdentity ? [Validators.required] : []);
        this.setValidators('primaryEmail', fullIdentity ? [this.nonBlank, Validators.email] : [Validators.email]);
        this.setValidators('postalCode', b2b ? [this.nonBlank] : []);
        this.setValidators('crNumber', b2b ? [Validators.required, gccCrNumberValidator(() => this.selectedCountry)] : [gccCrNumberValidator(() => this.selectedCountry)]);
        this.setValidators('taxRegNumber', b2b ? [this.nonBlank, gccVatNumberValidator(() => this.selectedCountry)] : [gccVatNumberValidator(() => this.selectedCountry)]);
        this.setValidators('name', this.mode === 'contact' ? [this.nonBlank] : []);
    }

    private refreshRegistrationValidators(): void {
        this.form.get('crNumber')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('taxRegNumber')?.updateValueAndValidity({ emitEvent: false });
    }

    private setValidators(name: string, validators: ValidatorFn[]): void {
        const control: AbstractControl | null = this.form.get(name);
        control?.setValidators(validators);
        control?.updateValueAndValidity({ emitEvent: false });
    }
}
